import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { Module, PermissionControlType, PermissionRole, PermissionValue } from 'app/core/models';
import { RolePermission } from '../../roles-permissions.types';
import { slideInOutHeight } from 'app/shared/functions/animation.functions';
import { Store } from '@ngxs/store';
import { AccountState } from '@state/account/state';
import { FeatureFlag } from '@core/enums';
import { PermissionRoleChangeArgs } from '../permission-value/permission-value.component';

type CheckboxState = 'checked' | 'unchecked' | 'indeterminate';
type RolePermissionGrouped = Module & { expanded: boolean; permissionsRoles: PermissionRole[] };

@Component({
  selector: 'app-settings-permissions-list',
  templateUrl: './permissions-list.component.html',
  styleUrls: ['./permissions-list.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [slideInOutHeight],
})
export class SettingsPermissionsListComponent implements OnInit {
  @Input() set permissionsRoles(value: PermissionRole[]) {
    this._permissionsRoles = value.sort(
      (a, b) => a.permission.permission_module.rank - b.permission.permission_module.rank,
    );

    this.setupTreeView();
  }

  @Input() readOnly = false;
  @Input() role: RolePermission;

  @Output() permissionsSaved = new EventEmitter<PermissionRole[]>();
  @Output() editRoleClicked = new EventEmitter<void>();
  @Output() removeRoleClicked = new EventEmitter<void>();

  modules: RolePermissionGrouped[] = [];
  PermissionControlType = PermissionControlType;
  hasNewPermissionsOn = false;

  private _permissionsRoles: PermissionRole[];

  constructor(private readonly store: Store) {}

  ngOnInit(): void {
    this.hasNewPermissionsOn = this.store.selectSnapshot(AccountState.isFeatureEnabled(FeatureFlag.NEW_PERMISSIONS));
  }

  onPermissionsSaved(): void {
    this.permissionsSaved.emit(this._permissionsRoles);
  }

  onPermissionChange(permissionChangeArgs: PermissionRoleChangeArgs): void {
    const permissionRole = this._permissionsRoles.find(({ id }) => id === permissionChangeArgs.permissionRole.id);

    if (permissionRole) {
      permissionRole.permission_value = permissionChangeArgs.payload;
    }
  }

  getModuleCheckboxState(module: RolePermissionGrouped = null): CheckboxState {
    return this.getCheckboxState(module?.permissionsRoles ?? []);
  }

  setModulePermissionsState(checked: boolean, module: RolePermissionGrouped): void {
    module.permissionsRoles.forEach((p) =>
      this.onPermissionChange({ permissionRole: p, payload: checked ? PermissionValue.True : PermissionValue.False }),
    );
    this.setupTreeView();
  }

  private setupTreeView(): void {
    this.modules = this._permissionsRoles
      .reduce((prev, curr) => {
        return prev.find(({ id }) => curr.permission.permission_module_id === id)
          ? prev
          : prev.concat(curr.permission.permission_module);
      }, [] as Module[])
      .map((module) => {
        return {
          ...module,
          expanded: true,
          permissionsRoles: this._permissionsRoles.filter(
            ({ permission: { permission_module_id } }) => permission_module_id === module.id,
          ),
        } as RolePermissionGrouped;
      });
  }

  private getCheckboxState(permissions: PermissionRole[]): CheckboxState {
    return permissions.every((permission) => permission.permission_value === PermissionValue.True)
      ? 'checked'
      : permissions.every((permission) => permission.permission_value === PermissionValue.False)
        ? 'unchecked'
        : 'indeterminate';
  }
}
